import React, { useEffect, useMemo, useRef } from "react";

const sanitize = (value) => (typeof value === "string" ? value.trim() : "");

const LookoutSection = ({ fields }) => {
    const sectionRef = useRef(null);

    const data = useMemo(() => fields || {}, [fields]);

    const logoUrl = data.logo?.url;
    const text = sanitize(data.text);
    const stats = Array.isArray(data.stats)
        ? data.stats.filter((stat) => sanitize(stat?.number) || sanitize(stat?.label))
        : [];
    const imageUrl = data.image?.url;

    const shouldRender = Boolean(logoUrl || text || stats.length || imageUrl);

    useEffect(() => {
        if (!shouldRender) return undefined;

        const section = sectionRef.current;
        if (!section) return undefined;

        const card = section.querySelector(".lookout-card");
        if (!card) return undefined;

        const onScroll = () => {
            // Use requestAnimationFrame to batch layout reads and prevent forced reflow
            requestAnimationFrame(() => {
                // Batch read: get all layout properties once
                const secRect = section.getBoundingClientRect();
                const cardRect = card.getBoundingClientRect();
                const viewH = window.innerHeight;

                const inView = secRect.top < viewH * 0.7 && secRect.bottom > viewH * 0.25;
                const covered = cardRect.top < 160;

                if (inView && !covered) {
                    section.classList.add("active");
                    section.classList.remove("covered");
                } else if (inView && covered) {
                    section.classList.add("covered");
                    section.classList.remove("active");
                } else {
                    section.classList.remove("active", "covered");
                }
            });
        };

        window.addEventListener("scroll", onScroll);
        onScroll();

        return () => {
            window.removeEventListener("scroll", onScroll);
        };
    }, [shouldRender]);

    if (!shouldRender) return null;

    return (
        <section className="section-lookout">
            <div className="canvas">
                <div className="lookout-inner">

                    {logoUrl && (
                        <div className="lookout-title" data-stagger-index="0">
                            <img src={logoUrl} alt={data.logo?.alt || "Lookout Logo"} />
                        </div>
                    )}

                    <div className="lookout-card" data-stagger-index="1">
                        <div className="lookout-left">
                            {text && <p className="lookout-text" data-stagger-index="0">{text}</p>}

                            {stats.length > 0 && (
                                <div className="lookout-stats" data-stagger-index="2">
                                    {stats.map((stat, index) => (
                                        <div className="stat" key={`${stat.number || "stat"}-${index}`}>
                                            {sanitize(stat.number) && <p className="stat-number">{stat.number}</p>}
                                            {sanitize(stat.label) && <p>{stat.label}</p>}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {imageUrl && (
                            <div className="lookout-right" data-stagger-index="1">
                                <img src={imageUrl} alt={data.image?.alt || "Vancouver Lookout"} />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default LookoutSection;

// lookout-title--sticky